import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { REPORT_ICONS } from './report.icon';
import {
  ChatMessage,
  ReportAssistantInput,
  ReportAssistantStats,
  SpecField
} from 'src/app/models/report-assistant.model';
import { SUGGESTED_QUESTIONS } from 'src/app/services/report-assistant/constant';
import { ReportAssistantService } from 'src/app/services/report-assistant/report-assistant.service';

/**
 * Slide-in chat panel that answers questions about the report on the page.
 * UI + chat state only: all analysis lives in ReportAssistantService.
 *
 * <app-report-assistant
 *   [open]="assistantOpen"
 *   [vinInfo]="vinInfo" [ratings]="ratings" [flags]="flags" [options]="options"
 *   [calculatedScore]="calculatedScore" [perceivedScore]="PerceivedScore"
 *   [specFields]="allSpecFields"
 *   [direction]="lang === 'ar' ? 'rtl' : 'ltr'"
 * ></app-report-assistant>
 *
 * The floating "Ask AI" button (bottom-right) belongs to this component; it opens/closes the panel
 * through the two-way [(open)] binding.
 */
@Component({
  selector: 'app-report-assistant',
  templateUrl: './report-assistant.component.html',
  styleUrls: ['./report-assistant.component.scss']
})
export class ReportAssistantComponent implements OnInit, OnChanges, OnDestroy {
  /** Panel visibility. Use as [(open)] */
  @Input() open = false;
  @Output() openChange = new EventEmitter<boolean>();

  // report data (same references the report page already holds)
  @Input() vinInfo: any;
  @Input() ratings: any;
  @Input() flags: any;
  @Input() options: any;
  @Input() calculatedScore: any;
  @Input() perceivedScore: any;
  @Input() specFields: SpecField[] = [];

  /** Text direction. The panel lives on <body> (see ngOnInit), so it cannot inherit it from the page. */
  @Input() direction: 'ltr' | 'rtl' = 'ltr';
  @HostBinding('attr.dir') get dir() {
    return this.direction;
  }

  @ViewChild('chatScroll') chatScroll: ElementRef<HTMLElement>;
  @ViewChild('chatInput') chatInput: ElementRef<HTMLInputElement>;

  public readonly icons = REPORT_ICONS;
  public readonly suggestedQuestions = SUGGESTED_QUESTIONS;

  public messages: ChatMessage[] = [];
  public typing = false;
  public draft = '';

  constructor(
    private assistant: ReportAssistantService,
    private host: ElementRef<HTMLElement>
  ) {}

  /**
   * The panel is position: fixed. If any ancestor of the page has a transform, filter, contain or
   * overflow (page wrappers and route animations often do), "fixed" stops meaning "the screen" and the
   * panel gets clipped or pushed off-screen. So, like a modal, it moves itself to <body>.
   */
  ngOnInit() {
    document.body.appendChild(this.host.nativeElement);
  }

  ngOnDestroy() {
    this.host.nativeElement.remove();
  }

  /** Current report data bundled for the service. Read lazily so late-loading data is always fresh. */
  private get report(): ReportAssistantInput {
    return {
      vinInfo: this.vinInfo,
      ratings: this.ratings,
      flags: this.flags,
      options: this.options,
      calculatedScore: this.calculatedScore,
      perceivedScore: this.perceivedScore,
      specFields: this.specFields
    };
  }

  get stats(): ReportAssistantStats {
    return this.assistant.getStats(this.report);
  }

  get brand(): string {
    const make = this.vinInfo?.['make_id']?.['value'];
    return make ? String(make) : 'car';
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['open']?.currentValue) {
      setTimeout(() => this.chatInput?.nativeElement.focus(), 350);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.open) this.close();
  }

  toggle() {
    this.openChange.emit(!this.open);
  }

  close() {
    this.openChange.emit(false);
  }

  clearChat() {
    this.messages = [];
  }

  sendDraft() {
    const q = this.draft.trim();
    if (!q) return;
    this.draft = '';
    this.ask(q);
  }

  async ask(question: string) {
    if (this.typing) return;
    this.messages.push({ role: 'user', text: question });
    this.typing = true;
    this.scrollChat();

    const started = Date.now();
    const reply = await this.assistant.ask(question, this.report, this.messages);

    // keep the typing indicator visible long enough to feel natural
    const wait = Math.max(0, 650 - (Date.now() - started));
    setTimeout(() => {
      this.typing = false;
      this.messages.push(reply);
      this.scrollChat();
    }, wait);
  }

  private scrollChat() {
    setTimeout(() => {
      const el = this.chatScroll?.nativeElement;
      if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }, 30);
  }
}
